import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';

const LOADING_WORDS = [
  'Thinking', 'Processing', 'Analyzing', 'Fetching',
  'Computing', 'Searching', 'Reasoning', 'Reflecting',
];

const SUGGESTIONS = [
  'What companies has Satyarth worked at?',
  'What AI systems has he built?',
  'Is he open to new opportunities?',
  "What's his educational background?",
  'What certifications does he have?',
];

function ChatContainer() {
  const [messages, setMessages] = useState([
    {
      sender: 'bot',
      text: "Hi! I'm Satyarth's AI assistant. Ask me anything about his career, projects, or experience.",
    },
  ]);
  const [userInput, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingWord, setLoadingWord] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(true);
  const loadingInterval = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const startLoading = () => {
    setIsLoading(true);
    setLoadingWord(LOADING_WORDS[Math.floor(Math.random() * LOADING_WORDS.length)]);
    loadingInterval.current = setInterval(() => {
      setLoadingWord(LOADING_WORDS[Math.floor(Math.random() * LOADING_WORDS.length)]);
    }, 600);
  };

  const stopLoading = () => {
    clearInterval(loadingInterval.current);
    setIsLoading(false);
    setLoadingWord('');
  };

  const sendMessage = async (text) => {
    const message = text !== undefined ? text : userInput;
    if (message.trim() === '' || isLoading) return;

    setShowSuggestions(false);
    setMessages((prev) => [...prev, { sender: 'user', text: message }]);
    setInput('');

    const url = process.env.REACT_APP_API_URL;
    if (!url) {
      setMessages((prev) => [
        ...prev,
        { sender: 'bot', text: 'Configuration error: API URL is not set.' },
      ]);
      return;
    }

    startLoading();
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: message, session_id: sessionId }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      setSessionId(data.session_id);
      stopLoading();
      setMessages((prev) => [...prev, { sender: 'bot', text: data.answer }]);
    } catch (error) {
      console.error('Error fetching bot response:', error);
      stopLoading();
      setMessages((prev) => [
        ...prev,
        { sender: 'bot', text: 'Sorry, something went wrong. Please try again later.' },
      ]);
    }
  };

  return (
    <div className="chat-container">
      <div className="chat-header">
        <div className="chat-header-dot" />
        <div className="chat-header-dot" />
        <div className="chat-header-dot" />
        <span className="chat-header-title">AI Career Assistant</span>
        <span className="chat-header-subtitle">Powered by Satyarth's experience</span>
      </div>

      <div className="chat-messages">
        {messages.map((msg, index) => (
          <div key={index} className={`chat-bubble-row ${msg.sender}`}>
            {msg.sender === 'bot' && <div className="bot-avatar">AI</div>}
            <div className={`chat-bubble ${msg.sender}`}>
              {msg.sender === 'bot'
                ? <ReactMarkdown>{msg.text}</ReactMarkdown>
                : msg.text}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="chat-bubble-row bot">
            <div className="bot-avatar">AI</div>
            <div className="chat-bubble bot loading-indicator">
              {loadingWord}
              <span className="loading-dots">...</span>
            </div>
          </div>
        )}

        {showSuggestions && !isLoading && (
          <div className="suggestions">
            <p className="suggestions-label">Try asking:</p>
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                className="suggestion-chip"
                onClick={() => sendMessage(s)}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input">
        <input
          type="text"
          value={userInput}
          placeholder="Ask about Satyarth's experience..."
          disabled={isLoading}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
        />
        <button
          className="send-button"
          onClick={() => sendMessage()}
          disabled={isLoading || userInput.trim() === ''}
        >
          {isLoading ? '…' : '→'}
        </button>
      </div>
    </div>
  );
}

export default ChatContainer;
